/**
 * CleanherbB Main JavaScript
 * Pre-launch landing page functionality
 *
 * Modules:
 *  1. Scroll Reveal
 *  2. Waitlist Form Handler
 *  3. Questionnaire Handler
 *  4. Smooth Scroll for anchor CTAs
 *  5. Checkbox max-select limiter (Q2 — pick up to 2)
 */

(function () {
  'use strict';

  /* ──────────────────────────────────────────────────────────
     1. SCROLL REVEAL
     Uses IntersectionObserver to trigger .reveal elements
     ────────────────────────────────────────────────────────── */
  function initScrollReveal() {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      // Immediately show all reveal elements
      document.querySelectorAll('.reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -40px 0px',
      }
    );

    document.querySelectorAll('.reveal').forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ──────────────────────────────────────────────────────────
     2. WAITLIST FORM HANDLER
     Handles all instances of the waitlist form on the page
     ────────────────────────────────────────────────────────── */
  function initWaitlistForms() {
    var forms = document.querySelectorAll('[data-waitlist-wrap]');

    forms.forEach(function (wrap) {
      var form = wrap.querySelector('.wl-form');
      if (!form) return;

      var emailInput = form.querySelector('input[type="email"]');
      var errorEl   = wrap.querySelector('.wl-form__error');
      var stepEmail = wrap.querySelector('[data-wl-step="email"]');
      var stepSuccess = wrap.querySelector('[data-wl-step="success"]');

      if (!form || !emailInput) return;

      // Client-side email validation
      function isValidEmail(email) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
      }

      function showError(message) {
        emailInput.classList.add('is-error');
        emailInput.setAttribute('aria-invalid', 'true');
        if (errorEl) {
          errorEl.textContent = message || 'Please enter a valid email address.';
          errorEl.classList.add('is-visible');
        }
      }

      function clearError() {
        emailInput.classList.remove('is-error');
        emailInput.removeAttribute('aria-invalid');
        if (errorEl) {
          errorEl.classList.remove('is-visible');
        }
      }

      // Real-time validation on blur
      emailInput.addEventListener('blur', function () {
        if (emailInput.value && !isValidEmail(emailInput.value)) {
          showError('Please enter a valid email address.');
        } else {
          clearError();
        }
      });

      emailInput.addEventListener('input', function () {
        if (emailInput.classList.contains('is-error')) {
          clearError();
        }
      });

      // Form submission — Shopify handles the actual POST
      // We intercept to show the success state after Shopify's redirect
      // For the pre-launch page, Shopify's customer form will POST and reload.
      // We detect the Shopify success param or use JS submission.
      form.addEventListener('submit', function (e) {
        e.preventDefault();

        var email = emailInput.value.trim();

        if (!email) {
          showError('Please enter your email address.');
          emailInput.focus();
          return;
        }

        if (!isValidEmail(email)) {
          showError('Please enter a valid email address.');
          emailInput.focus();
          return;
        }

        clearError();

        // Submit to Shopify via fetch
        var formData = new FormData(form);

        var submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = 'Joining…';
        }

        fetch(form.action || window.location.pathname, {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        })
          .then(function (response) {
            // Shopify returns various statuses; we treat any response as success
            // (duplicate emails are silently handled)
            showSuccessState(stepEmail, stepSuccess);
          })
          .catch(function () {
            // Network error fallback — still show success UX
            // (form data may have been submitted)
            showSuccessState(stepEmail, stepSuccess);
          });
      });
    });
  }

  function showSuccessState(stepEmail, stepSuccess) {
    if (stepEmail) {
      stepEmail.hidden = true;
    }
    if (stepSuccess) {
      stepSuccess.hidden = false;
      stepSuccess.focus();

      // Animate in
      var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!prefersReduced) {
        stepSuccess.style.animation = 'scaleIn 0.4s cubic-bezier(0.22,1,0.36,1) both';
      }
    }

    // Store in sessionStorage so page refreshes don't re-show the form
    try {
      sessionStorage.setItem('cleanherbb_waitlist_joined', '1');
    } catch (e) {}
  }

  // Check if already joined this session
  function checkAlreadyJoined() {
    try {
      if (sessionStorage.getItem('cleanherbb_waitlist_joined') === '1') {
        var wraps = document.querySelectorAll('[data-waitlist-wrap]');
        wraps.forEach(function (wrap) {
          var stepEmail   = wrap.querySelector('[data-wl-step="email"]');
          var stepSuccess = wrap.querySelector('[data-wl-step="success"]');
          if (stepEmail) stepEmail.hidden = true;
          if (stepSuccess) stepSuccess.hidden = false;
        });
      }
    } catch (e) {}
  }

  /* ──────────────────────────────────────────────────────────
     3. QUESTIONNAIRE HANDLER
     Show/hide the optional questionnaire after signup
     ────────────────────────────────────────────────────────── */
  function initQuestionnaire() {
    // Show questionnaire button
    document.querySelectorAll('[data-show-questionnaire]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var wrap = btn.closest('[data-waitlist-wrap]');
        if (!wrap) return;

        var qStep = wrap.querySelector('[data-wl-step="questionnaire"]');
        if (!qStep) return;

        qStep.hidden = false;
        btn.hidden = true;

        var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!prefersReduced) {
          qStep.style.animation = 'fadeInUp 0.5s cubic-bezier(0.22,1,0.36,1) both';
        }

        // Scroll to questionnaire
        setTimeout(function () {
          qStep.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
      });
    });

    // Questionnaire form submission
    document.querySelectorAll('[data-questionnaire-form]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        showQuestionnaireDone(form);
      });
    });

    // Skip questionnaire
    document.querySelectorAll('[data-questionnaire-skip]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var form = btn.closest('[data-questionnaire-form]');
        showQuestionnaireDone(form);
      });
    });
  }

  function showQuestionnaireDone(form) {
    if (!form) return;
    var qStep = form.closest('[data-wl-step="questionnaire"]');
    if (!qStep) return;

    var doneEl = qStep.querySelector('[data-questionnaire-done]');

    // Collect and store answers (for later Klaviyo/CRM integration)
    try {
      var formData = new FormData(form);
      var answers = {};
      formData.forEach(function (value, key) {
        if (answers[key]) {
          answers[key] = [].concat(answers[key], value);
        } else {
          answers[key] = value;
        }
      });

      // Store locally for now — integrate with Klaviyo/metafields later
      sessionStorage.setItem('cleanherbb_survey', JSON.stringify(answers));

      // Optional: POST to a Shopify endpoint or webhook
      // fetch('/apps/your-app/survey', { method: 'POST', body: JSON.stringify(answers) })
    } catch (e) {}

    if (form) form.hidden = true;
    if (doneEl) {
      doneEl.hidden = false;
      doneEl.focus();
    }
  }

  /* ──────────────────────────────────────────────────────────
     4. SMOOTH SCROLL — anchor CTAs
     ────────────────────────────────────────────────────────── */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(function (anchor) {
      anchor.addEventListener('click', function (e) {
        var targetId = anchor.getAttribute('href').slice(1);
        var target = document.getElementById(targetId);

        if (!target) return;

        e.preventDefault();

        var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        target.scrollIntoView({
          behavior: prefersReduced ? 'auto' : 'smooth',
          block: 'start',
        });

        // Focus management
        if (!target.hasAttribute('tabindex')) {
          target.setAttribute('tabindex', '-1');
        }
        target.focus({ preventScroll: true });
      });
    });
  }

  /* ──────────────────────────────────────────────────────────
     5. CHECKBOX MAX SELECT LIMITER (Q2 — pick up to 2)
     ────────────────────────────────────────────────────────── */
  function initCheckboxLimit() {
    document.querySelectorAll('[data-questionnaire-form]').forEach(function (form) {
      var q2group = form.querySelectorAll('input[name="q2_priority"]');

      if (!q2group.length) return;

      var MAX = 2;

      q2group.forEach(function (checkbox) {
        checkbox.addEventListener('change', function () {
          var checked = form.querySelectorAll('input[name="q2_priority"]:checked');

          if (checked.length >= MAX) {
            q2group.forEach(function (cb) {
              if (!cb.checked) {
                cb.disabled = true;
                cb.closest('.wl-q__option')
                  .querySelector('.wl-q__option-text')
                  .style.opacity = '0.4';
              }
            });
          } else {
            q2group.forEach(function (cb) {
              cb.disabled = false;
              cb.closest('.wl-q__option')
                .querySelector('.wl-q__option-text')
                .style.opacity = '';
            });
          }
        });
      });
    });
  }

  /* ──────────────────────────────────────────────────────────
     INIT
     ────────────────────────────────────────────────────────── */
  function init() {
    initScrollReveal();
    initWaitlistForms();
    checkAlreadyJoined();
    initQuestionnaire();
    initSmoothScroll();
    initCheckboxLimit();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
